/// <amd-module name="Widgets/AssignedToMeWidget/AssignedToMeQueries" />
/**
 *
 * Summary: Cacheable queries for fetching work items to be displayed by the Assigned to Me widget.
 *
 */

import { CategoryConfiguration } from "Dashboards/Clients/Contracts/Work";
import { Wiql, WorkItemQueryResult, WorkItemType } from "Dashboards/Clients/contracts/WorkItemTracking";
import { IWorkRestClient } from "Dashboards/Clients/Work";
import { ICacheableQuery } from "Reporting/Common/ICacheableQuery";
import { IProjectData } from "Tfs/Platform/Page";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { ExtendedError } from "WidgetCommon/CommonUtils/ExtendedError";
import { WorkItemTrackingClientName } from "Widgets/AssignedToMeWidget/clients/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Widgets/AssignedToMeWidget/clients/WorkItemTracking.Types";
import * as Resources from "Widgets/AssignedToMeWidget/Resources";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";

/**
 * This query retrieves a list of work items for given project and wiql.
 */
export class WorkItemsAssignedToMeQuery implements ICacheableQuery<WorkItemQueryResult> {
    constructor(private workItemTypes: WorkItemType[], private project: IProjectData) {}

    public runQuery(pageContext: IVssPageContext): Promise<WorkItemQueryResult> {
        const wiql = this.generateWiql(this.workItemTypes);
        const witRestClient = pageContext.getRestClient<IWorkItemTrackingRestClient>(WorkItemTrackingClientName);
        return witRestClient.queryByWiql(wiql, this.project.id);
    }

    public getKey(): string {
        return JSON.stringify({
            project: this.project.id,
            workItemTypes: this.workItemTypes
        });
    }

    // Generates a wiql for work items assigned to current user using work item types
    private generateWiql(workItemTypes: WorkItemType[]): Wiql {
        // This is used to build the final wiql
        const wiqlCoreTemplate =
            `SELECT [${CoreFieldRefNames.Id}],[${CoreFieldRefNames.State}],[${CoreFieldRefNames.Title}] ` +
            `from WorkItems where [${CoreFieldRefNames.TeamProject}] = '${this.project.name}' AND [${CoreFieldRefNames.AssignedTo}] = @me AND ({0})`;

        // This is used to build a condition clause for each work item type
        const typeInclusionConditionTemplate = `([${CoreFieldRefNames.WorkItemType}] = '{0}'{1})`;

        // Template for sorting the result
        const sortingConditionTemplate = ` ORDER BY [${CoreFieldRefNames.ChangedDate}] DESC`;

        // This is used to filter the terminal states for omission.
        const stateConditionTemplate = ` AND [${CoreFieldRefNames.State}] <> '{0}'`;

        // Check for terminal states which are irrelevant to the user and filter them out.
        const typeConditions: string[] = [];
        workItemTypes.forEach(workItemType => {
            const stateFilterConditions: string[] = [];
            const terminalStates = workItemType.states.filter(state => state.category === "Completed" || state.category === "Removed");
            terminalStates.forEach(terminalState => {
                stateFilterConditions.push(format(stateConditionTemplate, terminalState.name));
            });
            typeConditions.push(format(typeInclusionConditionTemplate, workItemType.name, stateFilterConditions.join("")));
        });

        return {
            query: format(wiqlCoreTemplate, typeConditions.join(" OR ")) + sortingConditionTemplate
        };
    }
}

/**
 * This query retrieves a list of work item types for a given project.
 */
export class WorkItemTypesQuery implements ICacheableQuery<string[]> {
    constructor(private projectId: string) {}

    public async runQuery(pageContext: IVssPageContext): Promise<string[]> {
        const workRestClient = pageContext.getRestClient<IWorkRestClient>("Dashboards-Work.IWorkRestClient");

        const processConfig = await workRestClient.getProcessConfiguration(this.projectId);
        const backlogs: (CategoryConfiguration | null)[] = [
            processConfig.taskBacklog,
            processConfig.requirementBacklog,
            ...processConfig.portfolioBacklogs,
            processConfig.bugWorkItems // May be null, such as in Basic Process
        ];

        return backlogs
            .filter(backlog => backlog != null)
            .map(backlog => backlog!.workItemTypes)
            .reduce((prev, curr) => prev.concat(curr))
            .map(workItemType => workItemType.name);
    }

    public getKey(): string {
        return JSON.stringify({
            project: this.projectId
        });
    }
}

export class WorkItemTypeDetailsQuery implements ICacheableQuery<WorkItemType[]> {
    constructor(private projectId: string, private workItemTypeNames: string[]) {}

    public async runQuery(pageContext: IVssPageContext): Promise<WorkItemType[]> {
        let fullWorkItemTypes: WorkItemType[] = [];
        const witRestClient = pageContext.getRestClient<IWorkItemTrackingRestClient>(WorkItemTrackingClientName);
        try {
            fullWorkItemTypes = await witRestClient.getWorkItemTypes(this.projectId);
        } catch (e) {
            const flattenedErrorString = JSON.stringify(e);
            const isNetworkException = flattenedErrorString != null && flattenedErrorString.indexOf("NetworkException") >= 0;
            const userFacingMessage = isNetworkException ? Resources.AssignedToMe_NetworkError : Resources.AssignedToMe_GenericError;
            throw new ExtendedError(userFacingMessage, e);
        }
        return fullWorkItemTypes.filter(fullWorkItemType => this.workItemTypeNames.indexOf(fullWorkItemType.name) !== -1);
    }

    public getKey(): string {
        return JSON.stringify({
            project: this.projectId,
            workItemTypeNames: this.workItemTypeNames
        });
    }
}
